import React, { useEffect, useMemo, useState } from "react";
import "../../assets/css/notificationDetail.css";
import { useNavigate, useParams } from "react-router-dom";
import { actionNotificacionesGetById } from "../../redux/actions/notificaciones/notificaciones";
import { useDispatch } from "react-redux";
import { relativeTime } from "../../utils/relativeTime";

function NotificationDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idNotificacion } = useParams();

  const [notificacion, setNotificacion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchNotificacion = async () => {
      try {
        setLoading(true);
        const resp = await dispatch(actionNotificacionesGetById(idNotificacion));

        if (mounted) {
          setNotificacion(resp || null);
        }
      } catch (error) {
        console.error("Error al obtener notificación:", error);
        if (mounted) {
          setNotificacion(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (idNotificacion) {
      fetchNotificacion();
    }

    return () => {
      mounted = false;
    };
  }, [dispatch, idNotificacion]);

  const fechaFormateada = useMemo(() => {
    return relativeTime(notificacion?.fecha_notificacion);
  }, [notificacion]);

  const usuariosLeyeron = useMemo(() => {
    if (!Array.isArray(notificacion?.usuarios_leyeron)) return [];
    return notificacion.usuarios_leyeron;
  }, [notificacion]);

  const usuariosNoLeyeron = useMemo(() => {
    if (!Array.isArray(notificacion?.usuarios_no_leyeron)) return [];
    return notificacion.usuarios_no_leyeron;
  }, [notificacion]);

  const tituloNotificacion = useMemo(() => {
    // El catálogo de tipos usa un nombre fijo ("Creación de expediente") para
    // este tipo aunque sea de Tribunal o una edición; se reemplaza por el
    // título real según la categoría real y la acción detectada.
    if (notificacion?.categoria === "expediente" && notificacion?.accion_detalle) {
      return `${notificacion.accion_detalle} de expediente`;
    }
    if (notificacion?.categoria === "audiencia") {
      return "Audiencia de Tribunal";
    }
    return notificacion?.nombre_tipo_notificacion || "Sin título";
  }, [notificacion]);

  const descripcionNotificacion = useMemo(() => {
    return notificacion?.descripcion || "Sin descripción disponible.";
  }, [notificacion]);

  const nombreModulo = useMemo(() => {
    return notificacion?.nombre_modulo || "Sin módulo";
  }, [notificacion]);

  const nombreUsuario = useMemo(() => {
    return notificacion?.nombre_usuario || "Usuario desconocido";
  }, [notificacion]);

  const detalleCampos = useMemo(() => {
    if (!Array.isArray(notificacion?.detalle_campos)) return [];
    return notificacion.detalle_campos;
  }, [notificacion]);

  const accionDetalle = useMemo(() => {
    return notificacion?.accion_detalle || null;
  }, [notificacion]);

  const fechaAccionTexto = useMemo(() => {
    return fechaFormateada ? `Notificado ${fechaFormateada}` : fechaFormateada;
  }, [fechaFormateada]);

  const expedienteRoute = useMemo(() => {
    if (!notificacion?.id_key) return null;
    if (notificacion.departamento === "conciliacion") return `/materias/laboral/centro-conciliacion/${notificacion.id_key}`;
    if (notificacion.departamento === "tribunal") return `/materias/laboral/tribunal/${notificacion.id_key}`;
    return null;
  }, [notificacion]);

  return (
    <div className="notification-page">
      <div className="layout">
        <main className="main-content">
          <div className="detail-content-wrapper">
            <div className="back-link-row">
            <button
  type="button"
  onClick={() => navigate("/notificaciones")}
  className="back-link btn"
>
                <svg
                  className="back-link-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M15 19l-7-7 7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
                <span>Regresar</span>
              </button>
            </div>

            <div className="page-header">
              <h1>Detalle de Notificación</h1>
              <p>Gestión integral de avisos procesales y términos legales</p>
            </div>

            <section className="notification-detail-card">
              <div className="card-topbar">
                <div className="card-topbar-left">
                  <span className="module-chip">{nombreModulo}</span>
                </div>

                <div className="card-topbar-right">
                  <div className="date-block">
                    <svg
                      className="date-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>{fechaAccionTexto}</span>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="notification-heading">
                  <h2>{tituloNotificacion}</h2>
                  <p>
                    {loading
                      ? "Cargando información de la notificación..."
                      : descripcionNotificacion}
                  </p>
                </div>

                <div className="info-grid">
                  <div className="info-section">
                    <h4>Información del Emisor</h4>

                    <div className="issuer-row">
                      <div className="issuer-avatar">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path
                            clipRule="evenodd"
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          />
                        </svg>
                      </div>

                      <div>
                        <p className="issuer-name">{nombreUsuario}</p>
                        <p className="issuer-role">{nombreModulo}</p>
                      </div>
                    </div>
                  </div>

                  <div className="info-section">
                    <h4>Leído por</h4>

                    <div className="read-by-list">
                      {usuariosLeyeron.length > 0 ? (
                        usuariosLeyeron.map((usuario) => (
                          <span
                            className="read-pill"
                            key={`leido-${usuario.id_user}`}
                            title={usuario.fecha_leido ? new Date(usuario.fecha_leido).toLocaleString("es-MX") : ""}
                          >
                            <div className="read-dot" />
                            {usuario.nombre}
                          </span>
                        ))
                      ) : (
                        <span className="read-pill more">Sin lecturas registradas</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="unread-section">
                  <h4>No leído por</h4>

                  <div className="read-by-list">
                    {usuariosNoLeyeron.length > 0 ? (
                      usuariosNoLeyeron.map((usuario) => (
                        <span
                          className="read-pill unread-pill"
                          key={`noleido-${usuario.id_user}`}
                        >
                          <div className="unread-dot" />
                          {usuario.nombre}
                        </span>
                      ))
                    ) : (
                      <span className="read-pill all-read-pill">
                        Todos han leído esta notificación
                      </span>
                    )}
                  </div>
                </div>

                <div className="description-box">
                  <h4>
                    {accionDetalle ? `Datos de la ${accionDetalle.toLowerCase()}` : "Descripción detallada"}
                  </h4>
                  {detalleCampos.length > 0 ? (
                    <dl className="detalle-campos-grid">
                      {detalleCampos.map((campo) => (
                        <div
                          className={`detalle-campo${campo.cambio ? " detalle-campo--cambio" : ""}`}
                          key={campo.campo}
                        >
                          <dt>
                            {campo.label}
                            {campo.cambio && <span className="cambio-badge">Editado</span>}
                          </dt>
                          {campo.cambio ? (
                            <dd>
                              <span className="valor-anterior">{campo.valor_anterior}</span>
                              <span className="valor-flecha">→</span>
                              <span className="valor-actual">{campo.valor}</span>
                            </dd>
                          ) : (
                            <dd>{campo.valor}</dd>
                          )}
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p>{descripcionNotificacion}</p>
                  )}
                </div>

                <div className="actions-row">
                  <button
                    className="primary-btn"
                    type="button"
                    disabled={!expedienteRoute}
                    onClick={() => expedienteRoute && navigate(expedienteRoute)}
                  >
                    <span>Ir al expediente</span>
                    <svg
                      className="action-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>

                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default NotificationDetail;